import type { FirebaseRuntimeConfig, LegionRuntimeConfig } from "../../lib/runtime-config.ts";
import { validateLegionRuntimeConfig } from "../../lib/runtime-config.ts";

type StoredSession = {
  idToken: string;
  refreshToken: string;
  expiresAt: number;
  uid: string;
  email: string | null;
};

export type IdTokenResult = {
  claims: Record<string, unknown>;
};

export type User = {
  uid: string;
  email: string | null;
  getIdToken: (forceRefresh?: boolean) => Promise<string>;
  getIdTokenResult: (forceRefresh?: boolean) => Promise<IdTokenResult>;
};

export type Auth = {
  currentUser: User | null;
};

type AuthListener = (user: User | null) => void;

type SignInResult = {
  user: User;
};

type FirebaseAuthApiError = Error & { code: string };

declare global {
  interface Window {
    __LEGION_RUNTIME_CONFIG__?: LegionRuntimeConfig;
  }
}

const SESSION_STORAGE_KEY = "legion_firebase_auth_session";
const REFRESH_BUFFER_MS = 60_000;
export let firebaseRuntimeConfigError: string | null = null;

function readRuntimeFirebaseConfig(): FirebaseRuntimeConfig {
  if (typeof window === "undefined") return {};
  return window.__LEGION_RUNTIME_CONFIG__?.firebase ?? {};
}

async function fetchAndApplyRuntimeConfig(): Promise<void> {
  if (typeof window === "undefined") return;
  if (window.__LEGION_RUNTIME_CONFIG__) {
    const validated = validateLegionRuntimeConfig(window.__LEGION_RUNTIME_CONFIG__);
    window.__LEGION_RUNTIME_CONFIG__ = validated.config;
    firebaseRuntimeConfigError = validated.error;
    return;
  }
  try {
    const res = await fetch("/runtime-config.json");
    if (res.ok) {
      const validated = validateLegionRuntimeConfig(await res.json());
      window.__LEGION_RUNTIME_CONFIG__ = validated.config;
      firebaseRuntimeConfigError = validated.error;
      if (firebaseRuntimeConfigError) {
        console.error(firebaseRuntimeConfigError);
      }
    }
  } catch {
    // Config fetch failed — firebase will be disabled
  }
}

await fetchAndApplyRuntimeConfig();

function isFirebaseClientConfigured(config: FirebaseRuntimeConfig): boolean {
  return Boolean(config.apiKey && config.projectId);
}

function createAuthError(code: string, message: string): FirebaseAuthApiError {
  const error = new Error(message) as FirebaseAuthApiError;
  error.code = code;
  return error;
}

function mapFirebaseErrorCode(rawCode: string): string {
  switch (rawCode) {
    case "INVALID_PASSWORD":
    case "EMAIL_NOT_FOUND":
    case "INVALID_LOGIN_CREDENTIALS":
      return "auth/invalid-credential";
    case "USER_DISABLED":
      return "auth/user-disabled";
    case "TOO_MANY_ATTEMPTS_TRY_LATER":
      return "auth/too-many-requests";
    case "EMAIL_EXISTS":
      return "auth/email-already-in-use";
    case "MISSING_PASSWORD":
      return "auth/missing-password";
    case "MISSING_EMAIL":
      return "auth/missing-email";
    case "INVALID_OOB_CODE":
      return "auth/invalid-action-code";
    case "EXPIRED_OOB_CODE":
      return "auth/expired-action-code";
    case "WEAK_PASSWORD":
      return "auth/weak-password";
    default:
      return "auth/internal-error";
  }
}

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    const rawPayload = parts[1];
    if (!rawPayload) return null;
    const normalized = rawPayload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    const json = atob(padded);
    const parsed = JSON.parse(json) as Record<string, unknown>;
    return parsed;
  } catch {
    return null;
  }
}

function readStoredSession(): StoredSession | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredSession;
    if (
      !parsed ||
      typeof parsed.idToken !== "string" ||
      typeof parsed.refreshToken !== "string" ||
      typeof parsed.expiresAt !== "number" ||
      typeof parsed.uid !== "string"
    ) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

function persistSession(session: StoredSession | null): void {
  if (typeof window === "undefined") return;
  try {
    if (!session) {
      localStorage.removeItem(SESSION_STORAGE_KEY);
      return;
    }
    localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
  } catch {
    // Ignore storage failures.
  }
}

const firebaseConfig = readRuntimeFirebaseConfig();
export const firebaseAuthEnabled =
  isFirebaseClientConfigured(firebaseConfig) && firebaseRuntimeConfigError === null;
export const app = null;

const authState: Auth = {
  currentUser: null,
};
export const auth = firebaseAuthEnabled ? authState : null;

let sessionRef: StoredSession | null = firebaseAuthEnabled ? readStoredSession() : null;
const listeners = new Set<AuthListener>();

function notifyListeners(): void {
  for (const listener of listeners) {
    listener(authState.currentUser);
  }
}

function setSession(session: StoredSession | null): void {
  sessionRef = session;
  persistSession(session);

  if (!session) {
    authState.currentUser = null;
    notifyListeners();
    return;
  }

  authState.currentUser = {
    uid: session.uid,
    email: session.email,
    getIdToken: async (forceRefresh = false) => {
      return getCurrentIdToken(forceRefresh);
    },
    getIdTokenResult: async (forceRefresh = false) => {
      const token = await getCurrentIdToken(forceRefresh);
      const claims = decodeJwtPayload(token) ?? {};
      return { claims };
    },
  };

  notifyListeners();
}

async function parseJsonResponse(response: Response): Promise<Record<string, unknown>> {
  return (await response.json().catch(() => ({}))) as Record<string, unknown>;
}

async function callIdentityToolkit(
  endpoint: string,
  payload: Record<string, unknown>
): Promise<Record<string, unknown>> {
  const apiKey = firebaseConfig.apiKey;
  if (!apiKey) {
    throw createAuthError(
      "auth/configuration-not-found",
      "Firebase client is not configured (missing API key)."
    );
  }

  const response = await fetch(
    `https://identitytoolkit.googleapis.com/v1/${endpoint}?key=${encodeURIComponent(apiKey)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    }
  );

  const body = await parseJsonResponse(response);
  if (!response.ok) {
    const message = String((body.error as { message?: string } | undefined)?.message ?? "UNKNOWN");
    const mappedCode = mapFirebaseErrorCode(message);
    throw createAuthError(mappedCode, message);
  }

  return body;
}

async function refreshSession(refreshToken: string): Promise<StoredSession> {
  const apiKey = firebaseConfig.apiKey;
  if (!apiKey) {
    throw createAuthError(
      "auth/configuration-not-found",
      "Firebase client is not configured (missing API key)."
    );
  }

  const response = await fetch(
    `https://securetoken.googleapis.com/v1/token?key=${encodeURIComponent(apiKey)}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: new URLSearchParams({
        grant_type: "refresh_token",
        refresh_token: refreshToken,
      }),
    }
  );

  const body = await parseJsonResponse(response);
  if (!response.ok) {
    const message = String((body.error as { message?: string } | undefined)?.message ?? "UNKNOWN");
    throw createAuthError("auth/token-refresh-failed", message);
  }

  const idToken = String(body.id_token ?? "");
  const newRefreshToken = String(body.refresh_token ?? refreshToken);
  const expiresIn = Number(body.expires_in ?? 0);
  const uid = String(body.user_id ?? "");
  const claims = decodeJwtPayload(idToken);
  const email = typeof claims?.email === "string" ? claims.email : (sessionRef?.email ?? null);

  if (!idToken || !uid || !Number.isFinite(expiresIn) || expiresIn <= 0) {
    throw createAuthError("auth/token-refresh-failed", "Invalid refresh response");
  }

  return {
    idToken,
    refreshToken: newRefreshToken,
    expiresAt: Date.now() + expiresIn * 1000,
    uid,
    email,
  };
}

async function getCurrentIdToken(forceRefresh = false): Promise<string> {
  if (!sessionRef) {
    throw createAuthError("auth/no-current-user", "No authenticated user.");
  }

  const shouldRefresh = forceRefresh || Date.now() + REFRESH_BUFFER_MS >= sessionRef.expiresAt;

  if (shouldRefresh) {
    try {
      const refreshed = await refreshSession(sessionRef.refreshToken);
      setSession(refreshed);
    } catch (error) {
      setSession(null);
      throw error;
    }
  }

  if (!sessionRef) {
    throw createAuthError("auth/no-current-user", "No authenticated user.");
  }

  return sessionRef.idToken;
}

if (sessionRef) {
  setSession(sessionRef);
}

export function ensureFirebaseAuth(): Auth {
  if (!firebaseAuthEnabled || !auth) {
    throw createAuthError(
      "auth/configuration-not-found",
      firebaseRuntimeConfigError ?? "Firebase auth is not configured for the frontend."
    );
  }
  return auth;
}

export function onAuthStateChanged(authInstance: Auth, callback: AuthListener): () => void {
  if (!firebaseAuthEnabled || auth !== authInstance) {
    callback(null);
    return () => undefined;
  }

  listeners.add(callback);
  callback(authInstance.currentUser);
  return () => {
    listeners.delete(callback);
  };
}

export async function signInWithEmailAndPassword(
  authInstance: Auth,
  email: string,
  password: string
): Promise<SignInResult> {
  if (!firebaseAuthEnabled || auth !== authInstance) {
    throw createAuthError(
      "auth/configuration-not-found",
      firebaseRuntimeConfigError ?? "Firebase auth is not configured for the frontend."
    );
  }

  const body = await callIdentityToolkit("accounts:signInWithPassword", {
    email,
    password,
    returnSecureToken: true,
  });

  const idToken = String(body.idToken ?? "");
  const refreshToken = String(body.refreshToken ?? "");
  const expiresIn = Number(body.expiresIn ?? 0);
  const uid = String(body.localId ?? "");
  const normalizedEmail = typeof body.email === "string" ? body.email : email;

  if (!idToken || !refreshToken || !uid || !Number.isFinite(expiresIn) || expiresIn <= 0) {
    throw createAuthError("auth/internal-error", "Invalid sign-in response");
  }

  setSession({
    idToken,
    refreshToken,
    expiresAt: Date.now() + expiresIn * 1000,
    uid,
    email: normalizedEmail,
  });

  if (!authInstance.currentUser) {
    throw createAuthError("auth/internal-error", "Failed to establish authenticated session");
  }

  return { user: authInstance.currentUser };
}

export async function signOut(authInstance: Auth): Promise<void> {
  if (!firebaseAuthEnabled || auth !== authInstance) return;
  setSession(null);
}

export async function sendPasswordResetEmail(authInstance: Auth, email: string): Promise<void> {
  if (!firebaseAuthEnabled || auth !== authInstance) {
    throw createAuthError(
      "auth/configuration-not-found",
      firebaseRuntimeConfigError ?? "Firebase auth is not configured for the frontend."
    );
  }

  await callIdentityToolkit("accounts:sendOobCode", {
    requestType: "PASSWORD_RESET",
    email,
  });
}

export async function verifyPasswordResetCode(oobCode: string): Promise<{ email: string | null }> {
  const body = await callIdentityToolkit("accounts:resetPassword", {
    oobCode,
  });

  return {
    email: typeof body.email === "string" ? body.email : null,
  };
}

export async function confirmPasswordReset(
  oobCode: string,
  newPassword: string
): Promise<{ email: string | null }> {
  const body = await callIdentityToolkit("accounts:resetPassword", {
    oobCode,
    newPassword,
  });

  return {
    email: typeof body.email === "string" ? body.email : null,
  };
}
